import { useEffect, useMemo, useRef, useState } from "react";
import { simulateFleet, type ShuttleLines, type SimVehicle } from "@chula/shared/shuttleSim";
import type { FallbackTier } from "@chula/shared";
import type { ShuttleVehicle } from "../map/layers";
import { simNow } from "../lib/simClock";

/** Poll cadence of /api/transit/cu-shuttle is 30 s; between polls a
 *  scenario-tier fleet is re-derived locally at this rate so buses glide
 *  rather than teleport. 8 fps is smooth at 20 km/h (≈0.7 m per frame at
 *  campus zoom) and costs nothing measurable. */
const SIM_FRAME_MS = 125;

interface ShuttleFeedLike {
  data: ShuttleVehicle[];
  fallbackTier: FallbackTier | "loading";
}

/**
 * The one seam in the "simulation → production" pipeline.
 *
 * - Feed tier `live` (ViaBus / CU IT GPS): the server payload is the truth;
 *   positions are passed through untouched and only change when a poll lands.
 * - Explicit `scenario` only: the browser runs the SAME pure engine the API ran
 *   (`@chula/shared/shuttleSim`) against the wall clock, so it lands on the
 *   positions the endpoint would report at that instant, refreshed every
 *   SIM_FRAME_MS. Nothing here invents motion the server would disagree with.
 *
 * Cached, unavailable and loading feeds keep their server positions; stale
 * GPS must never be replaced by unlabelled synthetic motion.
 * When a real GPS token arrives, the server-data branch simply starts winning;
 * no component above this hook changes.
 */
export function useShuttlePositions(feed: ShuttleFeedLike, lines: ShuttleLines | null): ShuttleVehicle[] {
  const isScenario = feed.fallbackTier === "scenario";
  const [tick, setTick] = useState(0);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (!isScenario || !lines) return;
    let last = 0;
    const loop = (t: number) => {
      if (t - last >= SIM_FRAME_MS) { last = t; setTick((n) => n + 1); }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => { if (raf.current != null) cancelAnimationFrame(raf.current); };
  }, [isScenario, lines]);

  return useMemo<ShuttleVehicle[]>(() => {
    if (!isScenario || !lines) return feed.data;
    return simulateFleet(lines, simNow()).map(toLayerVehicle);
    // `tick` is the animation clock — it is intentionally the dependency that
    // re-runs the engine, even though the value itself is unused.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScenario, lines, feed.data, tick]);
}

function toLayerVehicle(v: SimVehicle): ShuttleVehicle {
  return {
    id: v.id, line: v.line, lat: v.lat, lng: v.lng, bearing: v.bearing, occupancy: v.occupancy,
    speedKmh: v.speedKmh, atStop: v.atStop, nextStop: v.nextStop,
  };
}
